/**
 * Workflows Panel
 * 
 * Super admin interface for managing AI workflows:
 * - View all workflows across tool servers
 * - Create new workflows with visual step designer
 * - Edit workflow steps, conditions, and enforcement
 * - Manage organization access
 */

import React, { useState, useEffect, useCallback } from 'react';
import WorkflowDesigner from './WorkflowDesigner';
import './WorkflowsPanel.css';

interface ToolServer {
  id: number;
  name: string;
  url: string;
  is_active: boolean;
}

interface DomainInfo {
  id: string;
  name: string;
  icon: string;
  toolCount: number;
}

interface ToolInfo {
  name: string;
  description: string;
}

interface WorkflowStep {
  id?: number;
  stepOrder: number;
  domainId: string;
  toolName: string;
  description?: string;
  expectedOutput?: string;
  inputHint?: string;
  condition?: string;
  required: boolean;
  skipable: boolean;
  mustSucceed: boolean;
  onFailure: 'halt' | 'warn' | 'continue';
  alwaysRun: boolean;
}

interface WorkflowDataFlow {
  id?: number;
  sourceDomain?: string;
  sourceOutput?: string;
  targetDomain?: string;
  targetInput?: string;
  description?: string;
}

interface Workflow {
  id?: number;
  toolServerId?: number;
  workflowId: string;
  name: string;
  description?: string;
  triggerDescription?: string;
  enforcement: 'strict' | 'recommended' | 'flexible';
  isActive: boolean;
  steps: WorkflowStep[];
  dataFlows: WorkflowDataFlow[];
  createdBy?: number;
  createdAt?: string;
  updatedAt?: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const WorkflowsPanel: React.FC = () => {
  // Data state
  const [servers, setServers] = useState<ToolServer[]>([]);
  const [workflows, setWorkflows] = useState<Map<number, Workflow[]>>(new Map());
  const [domains, setDomains] = useState<Map<number, DomainInfo[]>>(new Map());
  const [domainTools, setDomainTools] = useState<Map<string, ToolInfo[]>>(new Map());
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Selection state
  const [selectedServer, setSelectedServer] = useState<ToolServer | null>(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  
  // Filter state
  const [filterEnforcement, setFilterEnforcement] = useState<string>('all');
  const [filterActive, setFilterActive] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch all tool servers
  const fetchServers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/tool-servers/all`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch tool servers');

      const data = await response.json();
      if (data.success) {
        const activeServers = data.servers.filter((s: ToolServer) => s.is_active);
        setServers(activeServers);
        
        // Auto-select first server if none selected
        if (activeServers.length > 0 && !selectedServer) {
          setSelectedServer(activeServers[0]);
        }
      }
    } catch (err: any) {
      setError(err.message);
    }
  }, [selectedServer]);

  // Fetch workflows for a server
  const fetchWorkflows = useCallback(async (serverId: number) => {
    try {
      const response = await fetch(`${API_URL}/tool-servers/${serverId}/workflows`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch workflows');

      const data = await response.json();
      if (data.success) {
        setWorkflows(prev => new Map(prev).set(serverId, data.workflows || []));
      }
    } catch (err: any) {
      console.error('Error fetching workflows:', err);
      // Don't show error for empty workflows
      setWorkflows(prev => new Map(prev).set(serverId, []));
    }
  }, []);

  // Fetch domains for a server
  const fetchDomains = useCallback(async (serverId: number, serverUrl: string) => {
    try {
      const response = await fetch(`${API_URL}/tool-servers/${serverId}/manifest`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch manifest');

      const data = await response.json();
      if (data.success && data.manifest) {
        setDomains(prev => new Map(prev).set(serverId, data.manifest.domains || []));
      }
    } catch (err: any) {
      console.error('Error fetching domains:', err);
    }
  }, []);

  // Fetch tools for a domain
  const fetchDomainTools = useCallback(async (serverId: number, domainId: string) => {
    const cacheKey = `${serverId}:${domainId}`;
    if (domainTools.has(cacheKey)) return;

    try {
      // We need to get the server URL first
      const server = servers.find(s => s.id === serverId);
      if (!server) return;

      // Fetch from the tool server directly via our proxy
      const response = await fetch(`${server.url}/domains/${domainId}`);
      if (!response.ok) throw new Error('Failed to fetch domain tools');

      const data = await response.json();
      if (data.tools) {
        setDomainTools(prev => new Map(prev).set(cacheKey, data.tools));
      }
    } catch (err: any) {
      console.error(`Error fetching tools for ${domainId}:`, err);
    }
  }, [servers, domainTools]);

  // Initial load
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await fetchServers();
      setLoading(false);
    };
    load();
  }, [fetchServers]);

  // Load data when server is selected
  useEffect(() => {
    if (selectedServer) {
      fetchWorkflows(selectedServer.id);
      fetchDomains(selectedServer.id, selectedServer.url);
    }
  }, [selectedServer, fetchWorkflows, fetchDomains]);

  // Create new workflow
  const handleCreateWorkflow = async (workflow: Workflow) => {
    if (!selectedServer) return;

    try {
      const response = await fetch(`${API_URL}/tool-servers/${selectedServer.id}/workflows`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(workflow)
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to create workflow');
      }

      setSuccess(`Workflow "${workflow.name}" created successfully!`);
      setIsCreating(false);
      setSelectedWorkflow(null);
      fetchWorkflows(selectedServer.id);
      
    } catch (err: any) {
      setError(err.message);
    }
  };

  // Update workflow
  const handleUpdateWorkflow = async (workflow: Workflow) => {
    if (!selectedServer || !workflow.id) return;

    try {
      const response = await fetch(`${API_URL}/tool-servers/${selectedServer.id}/workflows/${workflow.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(workflow)
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to update workflow');
      }

      setSuccess(`Workflow "${workflow.name}" updated successfully!`);
      setSelectedWorkflow(null);
      fetchWorkflows(selectedServer.id);
      
    } catch (err: any) {
      setError(err.message);
    }
  };

  // Delete workflow
  const handleDeleteWorkflow = async (workflowId: number) => {
    if (!selectedServer) return;
    
    if (!window.confirm('Are you sure you want to delete this workflow? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/tool-servers/${selectedServer.id}/workflows/${workflowId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete workflow');
      }

      setSuccess('Workflow deleted successfully!');
      fetchWorkflows(selectedServer.id);
      
    } catch (err: any) {
      setError(err.message);
    }
  };

  // Toggle workflow active status
  const handleToggleWorkflow = async (workflow: Workflow) => {
    if (!selectedServer || !workflow.id) return;

    try {
      const response = await fetch(`${API_URL}/tool-servers/${selectedServer.id}/workflows/${workflow.id}/toggle`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isActive: !workflow.isActive })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to toggle workflow');
      }

      fetchWorkflows(selectedServer.id);
      
    } catch (err: any) {
      setError(err.message);
    }
  };

  // Filter workflows
  const getFilteredWorkflows = (): Workflow[] => {
    if (!selectedServer) return [];
    
    let filtered = workflows.get(selectedServer.id) || [];
    
    if (filterEnforcement !== 'all') {
      filtered = filtered.filter(w => w.enforcement === filterEnforcement);
    }
    
    if (filterActive === 'active') {
      filtered = filtered.filter(w => w.isActive);
    } else if (filterActive === 'inactive') {
      filtered = filtered.filter(w => !w.isActive);
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(w => 
        w.name.toLowerCase().includes(term) ||
        w.workflowId.toLowerCase().includes(term) ||
        (w.description && w.description.toLowerCase().includes(term))
      );
    }
    
    return filtered;
  };

  // Auto-clear messages
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Get enforcement badge
  const getEnforcementBadge = (enforcement: string) => {
    switch (enforcement) {
      case 'strict':
        return <span className="enforcement-badge strict">🔒 Strict</span>;
      case 'recommended':
        return <span className="enforcement-badge recommended">📌 Recommended</span>;
      case 'flexible':
        return <span className="enforcement-badge flexible">💡 Flexible</span>;
      default:
        return null;
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div className="workflows-panel">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading workflows...</p>
        </div>
      </div>
    );
  }

  // Render designer if creating or editing
  if (isCreating || selectedWorkflow) {
    return (
      <div className="workflows-panel">
        <WorkflowDesigner
          workflow={selectedWorkflow || undefined}
          serverId={selectedServer?.id || 0}
          domains={selectedServer ? (domains.get(selectedServer.id) || []) : []}
          onSave={selectedWorkflow?.id ? handleUpdateWorkflow : handleCreateWorkflow}
          onCancel={() => {
            setIsCreating(false);
            setSelectedWorkflow(null);
          }}
          fetchDomainTools={(domainId) => {
            if (selectedServer) {
              fetchDomainTools(selectedServer.id, domainId);
            }
          }}
          getDomainTools={(domainId) => {
            if (!selectedServer) return [];
            return domainTools.get(`${selectedServer.id}:${domainId}`) || [];
          }}
        />
      </div>
    );
  }

  return (
    <div className="workflows-panel">
      {/* Messages */}
      {error && <div className="message error">{error}</div>}
      {success && <div className="message success">{success}</div>}

      {/* Header */}
      <div className="panel-header">
        <div>
          <h2>📋 Workflow Designer</h2>
          <p>Create and manage AI workflows with multi-step procedures and cross-domain orchestration</p>
        </div>
        <div className="header-actions">
          <button 
            className="btn primary"
            onClick={() => setIsCreating(true)}
            disabled={!selectedServer}
          >
            ➕ Create Workflow
          </button>
        </div>
      </div>

      {/* Server Selection */}
      <div className="server-selector">
        <label>Tool Server:</label>
        <div className="server-tabs">
          {servers.map(server => (
            <button
              key={server.id}
              className={`server-tab ${selectedServer?.id === server.id ? 'active' : ''}`}
              onClick={() => setSelectedServer(server)}
            >
              🖥️ {server.name}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <input
            type="text"
            placeholder="🔍 Search workflows..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filter-group">
          <label>Enforcement:</label>
          <select 
            value={filterEnforcement} 
            onChange={e => setFilterEnforcement(e.target.value)}
          >
            <option value="all">All</option>
            <option value="strict">🔒 Strict</option>
            <option value="recommended">📌 Recommended</option>
            <option value="flexible">💡 Flexible</option>
          </select>
        </div>
        
        <div className="filter-group">
          <label>Status:</label>
          <select 
            value={filterActive} 
            onChange={e => setFilterActive(e.target.value)}
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Workflows Grid */}
      <div className="workflows-grid">
        {getFilteredWorkflows().length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <h3>No Workflows Yet</h3>
            <p>Create your first workflow to define multi-step AI procedures.</p>
            <button 
              className="btn primary"
              onClick={() => setIsCreating(true)}
              disabled={!selectedServer}
            >
              ➕ Create Your First Workflow
            </button>
          </div>
        ) : (
          getFilteredWorkflows().map(workflow => (
            <div 
              key={workflow.id} 
              className={`workflow-card ${!workflow.isActive ? 'inactive' : ''}`}
            >
              <div className="workflow-header">
                <div className="workflow-title">
                  <h3>{workflow.name}</h3>
                  <code className="workflow-id">{workflow.workflowId}</code>
                </div>
                <div className="workflow-badges">
                  {getEnforcementBadge(workflow.enforcement)}
                  {!workflow.isActive && <span className="status-badge inactive">Inactive</span>}
                </div>
              </div>
              
              {workflow.triggerDescription && (
                <div className="workflow-trigger">
                  <strong>🎯 Trigger:</strong> {workflow.triggerDescription}
                </div>
              )}
              
              {workflow.description && (
                <p className="workflow-description">{workflow.description}</p>
              )}
              
              <div className="workflow-steps-preview">
                <div className="steps-header">
                  <span>📝 {workflow.steps.length} Steps</span>
                  {workflow.dataFlows.length > 0 && (
                    <span>🔗 {workflow.dataFlows.length} Data Flows</span>
                  )}
                </div>
                <div className="steps-mini">
                  {workflow.steps.slice(0, 4).map((step, idx) => (
                    <div key={idx} className="step-mini">
                      <span className="step-num">{step.stepOrder}</span>
                      <span className="step-tool">{step.toolName}</span>
                      <span className="step-domain">{step.domainId}</span>
                    </div>
                  ))}
                  {workflow.steps.length > 4 && (
                    <div className="step-mini more">+{workflow.steps.length - 4} more</div>
                  )}
                </div>
              </div>
              
              <div className="workflow-actions">
                <button 
                  className="action-btn edit"
                  onClick={() => setSelectedWorkflow(workflow)}
                >
                  ✏️ Edit
                </button>
                <button 
                  className="action-btn duplicate"
                  onClick={() => {
                    setSelectedWorkflow({
                      ...workflow,
                      id: undefined,
                      workflowId: `${workflow.workflowId}-copy`,
                      name: `${workflow.name} (Copy)`
                    });
                    setIsCreating(true);
                  }}
                >
                  📋 Clone
                </button>
                <button 
                  className={`action-btn ${workflow.isActive ? 'deactivate' : 'activate'}`}
                  onClick={() => handleToggleWorkflow(workflow)}
                >
                  {workflow.isActive ? '⏸️ Disable' : '▶️ Enable'}
                </button>
                <button 
                  className="action-btn delete"
                  onClick={() => workflow.id && handleDeleteWorkflow(workflow.id)}
                >
                  🗑️
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default WorkflowsPanel;

